import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Api from "../../../services/Api";
import { queryKeys } from "../../../lib/query";

const HYBRID_URL = "/service/iqevse-csms/api/home/search/hybrid";

/**
 * Fetch chargers from the hybrid search API (OpenSearch + MongoDB).
 *
 * Supports server-side filtering, text search, pagination, sorting,
 * and date-range filtering.
 */
async function fetchHybridSearch({ query, filters, page, size, sort, dateField, fromDate, toDate }) {
  const body = {};
  if (query) body.query = query;
  if (filters && Object.keys(filters).length > 0) body.filters = filters;
  if (page) body.page = page;
  if (size) body.size = size;
  if (sort) body.sort = sort;
  if (dateField) body.dateField = dateField;
  if (fromDate) body.fromDate = fromDate;
  if (toDate) body.toDate = toDate;

  const res = await Api.post(HYBRID_URL, body);
  return res;
}

/**
 * Normalise a single hybrid-API charger record from snake_case → camelCase
 * so the DataTable column keys remain consistent with V1 where possible.
 */
function normaliseCharger(raw) {
  return {
    id: raw.id,
    serialNumber: raw.serial_number ?? raw.serialNumber,
    siteId: raw.site_id ?? raw.siteId,
    vendorName: raw.vendor_name ?? raw.vendorName,
    modelName: raw.model_name ?? raw.modelName,
    displayName: raw.display_name ?? raw.displayName,
    country: raw.country,
    protocol: raw.protocol,
    protocolVersion: raw.protocol_version ?? raw.protocolVersion,
    isConnected: raw.is_connected ?? raw.isConnected,
    isRetired: raw.is_retired ?? raw.isRetired,
    isFaulted: raw.is_faulted ?? raw.isFaulted,
    isDeviceReady: raw.is_device_ready ?? raw.isDeviceReady,
    // Fields available in actual API response
    fwVersion: raw.fw_version ?? raw.fwVersion,
    lastReportedAt: raw.last_reported_at ?? raw.lastReportedAt,
    createdAt: raw.created_at ?? raw.createdAt,
    updatedAt: raw.updated_at ?? raw.updatedAt,
    phaseCount: raw.phase_count ?? raw.phaseCount,
    maxCurrent: raw.max_current ?? raw.maxCurrent,
    breakerCurrent: raw.breaker_current ?? raw.breakerCurrent,
    operatingVoltage: raw.operating_voltage ?? raw.operatingVoltage,
    location: raw.location,
    address: raw.address,
    connectionType: raw.connection_type ?? raw.connectionType,
    dynamicUpdatedAt: raw.dynamic_updated_at ?? raw.dynamicUpdatedAt,
    // V2-specific fields
    city: raw.city,
    state: raw.state,
    zip: raw.zip,
    owner: raw.owner,
    operationStatus: raw.operation_status ?? raw.operationStatus,
    chargingStatus: raw.charging_status ?? raw.chargingStatus,
    connectorIsPluggedIn: raw.connector_is_plugged_in ?? raw.connectorIsPluggedIn,
    connectorStatusReason: raw.connector_status_reason ?? raw.connectorStatusReason,
    gatewayConnectivityStatus: raw.gateway_connectivity_status ?? raw.gatewayConnectivityStatus,
    sessionState: raw.session_state ?? raw.sessionState,
  };
}

/**
 * Hook for the hybrid search API.
 *
 * Unlike V1 (which loads all chargers client-side), V2 relies on
 * server-side pagination, filtering, and sorting.
 *
 * Returns: { data, total, page, size, totalPages, strategy, isLoading, isError, refetch, setSearchParams }
 */
export default function useHybridSearch(initialParams = {}) {
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useState({
    query: "",
    filters: {},
    page: 1,
    size: 25,
    sort: "",
    dateField: "",
    fromDate: "",
    toDate: "",
    ...initialParams,
  });

  // Stable query key based on current search params
  const queryKey = useMemo(() => queryKeys.fleetHybrid.search(searchParams), [searchParams]);

  const {
    data: response,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: () => fetchHybridSearch(searchParams),
    staleTime: 30000, // Keep data fresh for 30 seconds
    gcTime: 60000, // Keep in cache for 60 seconds (formerly cacheTime)
    keepPreviousData: true,
  });

  const normalisedData = useMemo(() => {
    const raw = response?.data ?? [];
    return raw.map(normaliseCharger);
  }, [response]);

  const result = useMemo(() => {
    const total = response?.total ?? 0;
    const size = response?.size ?? searchParams.size;
    // Compute totalPages from total/size if API doesn't return it
    const computedTotalPages = size > 0 ? Math.ceil(total / size) : 0;
    const apiTotalPages = response?.total_pages || response?.totalPages || 0;

    return {
      data: normalisedData,
      total,
      page: response?.page ?? searchParams.page,
      size,
      totalPages: apiTotalPages || computedTotalPages,
      strategy: response?.strategy ?? "",
      isLoading,
      isError,
    };
  }, [normalisedData, response, searchParams, isLoading, isError]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.fleetHybrid.all });
  }, [queryClient]);

  return { ...result, searchParams, setSearchParams, refetch };
}
